import cloneDeep from "lodash.clonedeep";
import { deriveEndpoint } from "../property/structure";
import { editItem } from "./pages";

// How an experiment ends: one variation is promoted (its page becomes the
// default page of the endpoint), or it is closed with no winner. Either way
// the experiment is archived: it keeps its outcome, loses its reference
// (builder-block ignores it, the endpoint is free for another experiment)
// and its pages are unlocked. Nothing is deleted: the pages that don't win
// stay as disabled variants of the endpoint, to be removed by hand.

export const promotedOutcome = (variation, now = new Date()) => ({
  action: "promoted",
  variationId: variation.id,
  at: now.toISOString()
});

export const closedOutcome = (now = new Date()) => ({
  action: "closed",
  at: now.toISOString()
});

// The experiment as archived with the outcome (the form's values, or the
// stored row)
export function archive(experiment, outcome) {
  const { reference, ...rest } = experiment;

  return {
    ...rest,
    outcome,
    running: false,
    archivedReference: reference ?? experiment.archivedReference
  };
}

// The endpoint once `variation` is promoted: its page is the default one,
// out of the variants; the page that was the default one becomes an inactive
// variant, and the other pages stay the inactive variants they are. null for
// the control: its page is the default one already, nothing changes.
// endpoint: the stored row. doRefs: the refs of an endpoint (doEndpointRefs).
export function promotedEndpoint(endpoint, experiment, variation, doRefs) {
  if (variation.control) return null;
  if (!endpoint)
    throw new Error("The endpoint of the experiment doesn't exist anymore");

  const control = experiment.variations?.find((one) => one.control);
  if (control && control.blockId !== endpoint.blockId)
    throw new Error(
      "The default page of the endpoint is no longer the control of the experiment"
    );

  const variants = endpoint.variants ?? [];
  if (!variants.some((variant) => variant.blockId === variation.blockId))
    throw new Error(
      `The page of "${variation.label}" is no longer a variant of the endpoint`
    );

  const row = cloneDeep(endpoint);
  row.variants = [
    ...variants.filter((variant) => variant.blockId !== variation.blockId),
    { blockId: endpoint.blockId, condition: "*", priority: 1, active: false }
  ];
  row.blockId = variation.blockId;

  return deriveEndpoint(row, cloneDeep(endpoint), endpoint, doRefs);
}

// The rows the Save of the experiment sends with it to promote a variation:
// the endpoint, only on the Save that archives the experiment (the stored one
// has no outcome yet). data: the values of the Save. stored: the stored
// experiment. endpoint: the stored row of the endpoint.
export function outcomeItems({ data, stored, endpoint, doRefs }) {
  const outcome = data?.outcome;
  if (outcome?.action !== "promoted" || stored?.outcome) return [];

  const variation = data.variations?.find(
    (one) => one.id === outcome.variationId
  );
  if (!variation)
    throw new Error("The promoted variant is not in the experiment");

  const row = promotedEndpoint(endpoint, data, variation, doRefs);

  return row ? [editItem("endpoints", row)] : [];
}
